import { h, safeUrl, type Child } from "../shared/safe";
import { noticeBlock } from "../client/bits";
import type { Notice } from "../client/context";
import type { Problem } from "./factsModel";

// Small building blocks shared by the facts cards. Everything is built with h(): text is text, links are validated.

let counter = 0;
export const uid = (prefix: string) => `${prefix}-${++counter}`;

/** A labelled control with optional help text and an inline error that is announced. */
export function labelled(label: string, input: HTMLElement, o: { help?: string; error?: string | null; wide?: boolean } = {}): HTMLElement {
  const id = input.id || uid("fx");
  input.id = id;
  const helpId = o.help ? `${id}-help` : null;
  const errId = o.error ? `${id}-err` : null;
  const describedBy = [helpId, errId].filter(Boolean).join(" ");
  if (describedBy) input.setAttribute("aria-describedby", describedBy);
  if (o.error) input.setAttribute("aria-invalid", "true");
  return h(
    "div",
    { class: `fx-field${o.wide ? " fx-field--wide" : ""}` },
    h("label", { for: id }, label),
    input,
    o.help ? h("div", { class: "fx-help", id: helpId ?? undefined }, o.help) : null,
    o.error ? h("div", { class: "fx-error", id: errId ?? undefined, role: "alert" }, o.error) : null,
  );
}

export function textInput(value: string, o: { name?: string; placeholder?: string; type?: string; onInput?: (v: string) => void } = {}): HTMLInputElement {
  const el = h("input", { type: o.type ?? "text", value, placeholder: o.placeholder, autocomplete: "off", spellcheck: "false", dataset: { field: o.name } }) as HTMLInputElement;
  if (o.onInput) el.addEventListener("input", () => o.onInput!(el.value));
  return el;
}
export function textArea(value: string, o: { name?: string; rows?: number; onInput?: (v: string) => void; mono?: boolean } = {}): HTMLTextAreaElement {
  const el = h("textarea", { rows: o.rows ?? 3, spellcheck: "false", class: o.mono ? "fx-mono" : false, dataset: { field: o.name } }) as HTMLTextAreaElement;
  el.value = value;
  if (o.onInput) el.addEventListener("input", () => o.onInput!(el.value));
  return el;
}
export function select(options: readonly (string | [string, string])[], value: string, o: { name?: string; onChange?: (v: string) => void } = {}): HTMLSelectElement {
  const el = h(
    "select",
    { dataset: { field: o.name } },
    ...options.map((opt) => {
      const [v, text] = typeof opt === "string" ? [opt, opt] : opt;
      return h("option", { value: v }, text);
    }),
  ) as HTMLSelectElement;
  el.value = value;
  if (o.onChange) el.addEventListener("change", () => o.onChange!(el.value));
  return el;
}

/**
 * A button that cannot be double-submitted: while its action runs it is aria-disabled (never `disabled`, so keyboard focus stays) and
 * says what it is doing. `run` resolves when the action is done.
 */
export function busyButton(o: { label: string; busyLabel: string; onClick: () => Promise<void> | void; variant?: "secondary" | "danger" | "ghost"; id?: string; type?: "button" | "submit"; ariaLabel?: string }): HTMLButtonElement & { setBusy(b: boolean): void } {
  let busy = false;
  const el = h("button", { type: o.type ?? "button", class: o.variant ?? false, id: o.id, "aria-label": o.ariaLabel }, o.label) as HTMLButtonElement & { setBusy(b: boolean): void };
  el.setBusy = (b) => {
    busy = b;
    el.textContent = b ? o.busyLabel : o.label;
    if (b) {
      el.setAttribute("aria-busy", "true");
      el.setAttribute("aria-disabled", "true");
    } else {
      el.removeAttribute("aria-busy");
      el.removeAttribute("aria-disabled");
    }
  };
  el.addEventListener("click", async (ev) => {
    if (busy) return void ev.preventDefault();
    el.setBusy(true);
    try {
      await o.onClick();
    } finally {
      el.setBusy(false);
    }
  });
  return el;
}

export function notice(tone: Notice["tone"], text: string, detail?: string[]): HTMLElement {
  return noticeBlock({ tone, text, detail })!;
}
export function problemNotice(p: Problem): HTMLElement {
  return notice(p.kind === "stale" || p.kind === "network" || p.kind === "server" ? "warn" : "bad", p.message, p.issues.map((i) => (i.field ? `${i.field}: ${i.message}` : i.message)));
}

export function webLink(url: string | null, text?: string): Child {
  if (url === null) return "—";
  try {
    const u = new URL(url.trim());
    if (u.protocol === "http:" || u.protocol === "https:") return h("a", { href: safeUrl(u.href), target: "_blank", rel: "noopener noreferrer" }, text ?? url);
  } catch {
    /* not a URL */
  }
  return h("span", { title: "Not a web address, so it is shown as text" }, text ?? url);
}

/** A card in the existing visual language, with a heading that names it. */
export function card(id: string, title: string, ...children: Child[]): HTMLElement {
  return h("section", { class: "card qf-cd-card fx-card", id, "aria-labelledby": `${id}-h` }, h("h2", { id: `${id}-h` }, title), ...children);
}

/** Text for who did something: the local boundary's labels are spelled out. */
export function whoText(by: string | null): string {
  if (by === null) return "unknown";
  if (by === "local-dashboard") return "local-dashboard (this dashboard)";
  if (by === "local-mcp") return "local-mcp (an AI client)";
  return by;
}
